import React, { useState, useMemo, useRef } from "react";
import { 
  Search, 
  X, 
  Check, 
  AlertCircle, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Briefcase, 
  Users, 
  Phone, 
  ShoppingBag, 
  ArrowRight,
  UserCheck,
  Calendar,
  Layers,
  FileText,
  Download
} from "lucide-react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency } from "@/components/common";
import { useAuthStore } from "@/store/authStore";
import { exportToPDF } from "../../../utils/exportUtils";
import FilterSection from "../../../components/ui/FilterSection";
import toast from "react-hot-toast";

// Predefined workflow levels
const WORKFLOW_LEVELS = [
  { id: "started", label: "Started", desc: "Project Initialized" },
  { id: "designed", label: "Designed", desc: "Design & Estimate Approved" },
  { id: "materials_ordered", label: "Materials Ordered", desc: "Factory Materials Placed" },
  { id: "installation", label: "Installation", desc: "On-site Installation" },
  { id: "completed", label: "Completed", desc: "Handover Complete" }
];

export const OwnerDashboard = () => {
  const { user: authUser } = useAuthStore();
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [showWorkflowSearch, setShowWorkflowSearch] = useState(false);
  const [trackedProject, setTrackedProject] = useState(null);
  const [workflowError, setWorkflowError] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ date: 'all', fromDate: '', toDate: '' });
  const [isExporting, setIsExporting] = useState(false);

  const page1Ref = useRef(null);
  const page2Ref = useRef(null);

  const {
    analytics,
    recentLeads,
    activeProjects,
    allLeads,
    allProjects,
    users,
    loading
  } = useDashboard(search, filters);

  const handleExport = async () => {
    if (loading || isExporting || !page1Ref.current || !page2Ref.current) return;
    try {
      setIsExporting(true);
      
      const pad = n => String(n).padStart(2, '0');
      const d = new Date();
      const dateStr = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      
      await exportToPDF([page1Ref.current, page2Ref.current], `Owner_Dashboard_Export_${dateStr}.pdf`);
      toast.success('Dashboard exported to PDF successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export dashboard PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // 1. Project Workflow Status Mapper
  const getProjectWorkflowLevel = (status) => {
    const st = (status || "").toLowerCase().trim();
    if (["completed", "handover"].includes(st)) return 4; // Completed
    if (["installation_in_progress", "carpenter complete", "electrician complete", "painter complete", "plumber complete", "installation"].includes(st)) return 3; // Installation
    if (["materials_requested", "material requested", "pending store", "owner approval", "dispatched", "materials ordered", "materials"].includes(st)) return 2; // Materials Ordered
    if (["design", "design_approval", "cust_acceptance", "customer_acceptance", "designed"].includes(st)) return 1; // Designed
    return 0; // Started
  };

  // Handle Workflow search
  const handleWorkflowSearchSubmit = (e) => {
    e.preventDefault();
    if (!workflowSearch.trim()) {
      setWorkflowError("Please enter a valid Verve ID");
      setTrackedProject(null);
      return;
    }

    const query = workflowSearch.toLowerCase().trim();
    // Search in allLeads (which includes projects)
    const match = allLeads.find(l => {
      const vid = (l.customId || l.projectId || l.projectNumber || l.leadId || l._id || "").toLowerCase();
      const name = (l.customerName || "").toLowerCase();
      return vid.includes(query) || name.includes(query);
    });

    if (match) {
      setTrackedProject(match);
      setWorkflowError("");
    } else {
      setTrackedProject(null);
      setWorkflowError(`No project found matching "${workflowSearch}"`);
    }
  };

  // Clear workflow search
  const clearWorkflowSearch = () => {
    setWorkflowSearch("");
    setTrackedProject(null);
    setWorkflowError("");
  };

  // 2. Combined team workload & daily attendance list (Syncing names with frontend team page, emp_id removed)
  const combinedTeamList = useMemo(() => {
    // Attendance map for status & phone fallback
    const attendanceMap = {
      "designer": { status: "Present", phone: "+91 98765 43212" },
      "Site Exec": { status: "Present", phone: "+91 98765 43213" },
      "Vinay R": { status: "Present", phone: "+91 98765 43214" },
      "Dharani K": { status: "Absent", phone: "+91 98765 43215" }
    };

    // Fallback mock team in case the database contains no users
    const fallbackTeam = [
      { name: "designer", phone: "+91 98765 43212", status: "Present", totalProjects: 24, assigned: 16, ongoing: 6, completed: 2 },
      { name: "Site Exec", phone: "+91 98765 43213", status: "Present", totalProjects: 15, assigned: 10, ongoing: 3, completed: 2 },
      { name: "Vinay R", phone: "+91 98765 43214", status: "Present", totalProjects: 18, assigned: 11, ongoing: 5, completed: 2 },
      { name: "Dharani K", phone: "+91 98765 43215", status: "Absent", totalProjects: 9, assigned: 6, ongoing: 2, completed: 1 }
    ];

    if (!users || users.length === 0) {
      return fallbackTeam;
    }

    // Map live backend users (syncing names with frontend team page)
    return users.map((u, index) => {
      const fullName = u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown User";
      
      // Calculate workload statistics from allLeads/allProjects for this user
      const userLeads = allLeads.filter(l => {
        const assigned = l.assignedTo || l.salesPerson || l.telecaller;
        if (assigned && assigned._id) return String(assigned._id) === String(u._id);
        return String(assigned) === String(u._id);
      });

      const totalProjects = userLeads.length;
      
      const projectStages = ['cust_acceptance', 'design', 'design_approval', 'final_quotation', 'quotation_approval', 'documents', 'project_started', 'project', 'customer_acceptance'];
      const converted = userLeads.filter(l => l.isMovedToProject === true || l.isMovedToProject === 'true' || projectStages.includes(l.leadStatus) || projectStages.includes(l.status)).length;
      const assigned = Math.max(0, totalProjects - converted);
      const completed = userLeads.filter(l => ['completed'].includes(l.projectStatus || l.status)).length;
      const ongoing = Math.max(0, converted - completed);

      const statusMap = attendanceMap[fullName] || {
        status: index % 5 === 0 ? "Absent" : "Present",
        phone: u.phoneNumber || u.phone || "+91 98765 43210"
      };

      return {
        name: fullName,
        phone: u.phone || u.phoneNumber || statusMap.phone,
        status: statusMap.status,
        totalProjects: totalProjects || fallbackTeam.find(f => f.name === fullName)?.totalProjects || 0,
        assigned: assigned || fallbackTeam.find(f => f.name === fullName)?.assigned || 0,
        ongoing: ongoing || fallbackTeam.find(f => f.name === fullName)?.ongoing || 0,
        completed: completed || fallbackTeam.find(f => f.name === fullName)?.completed || 0
      };
    });
  }, [users, allLeads]);

  // 4. Financial Summary Metrics
  const financialMetrics = useMemo(() => {
    return [
      { 
        label: "Project Sales", 
        value: analytics?.totalProjectRevenue || 1850000, 
        desc: "Payments received from active interior projects",
        color: "from-teal-50 to-teal-100/50 text-teal-800 border-teal-200" 
      },
      { 
        label: "Direct Sales", 
        value: analytics?.totalSales || 1050000, 
        desc: "Direct appliance and modular components sales",
        color: "from-slate-50 to-slate-100/50 text-slate-800 border-slate-200" 
      },
      { 
        label: "Total Expenses", 
        value: analytics?.totalExpenses || 740000, 
        desc: "Materials, project costs, and general expenses",
        color: "from-red-50 to-red-100/50 text-red-700 border-red-200" 
      },
      { 
        label: "Net Profit", 
        value: analytics?.netProfit || 2160000, 
        desc: "Net surplus (Revenue - Total Expenses)",
        color: "from-amber-50 to-amber-100/50 text-amber-800 border-amber-200 accent-gold" 
      }
    ];
  }, [analytics]);

  // 5. Operations Metrics
  const operationsMetrics = useMemo(() => {
    return [
      { label: "Total Projects", value: analytics?.totalProjects || 14, icon: <Briefcase size={20} className="text-teal-600" /> },
      { label: "Total Pipeline Cost", value: analytics?.totalPipelineValue || 3500000, icon: <Layers size={20} className="text-teal-600" />, isCurrency: true },
      { label: "Total Contacts", value: analytics?.totalContacts || 84, icon: <Users size={20} className="text-teal-600" /> },
      { label: "Total Leads", value: analytics?.totalLeads || 48, icon: <FileText size={20} className="text-teal-600" /> }
    ];
  }, [analytics]);

  // 6. General Expenses Mock Data (Non-project monthly operational expenses)
  const generalExpenses = [
    { id: "GE-01", category: "Office Rent", desc: "Main studio monthly rent (Jubilee Hills)", date: "2026-07-01", amount: 120000 },
    { id: "GE-02", category: "Utility Bills", desc: "Electricity & High-speed internet setup", date: "2026-07-04", amount: 18500 },
    { id: "GE-03", category: "Software Licenses", desc: "AutoCAD & SketchUp monthly subscription", date: "2026-07-05", amount: 32000 },
    { id: "GE-04", category: "Marketing & Ads", desc: "Facebook and Instagram campaign management", date: "2026-07-08", amount: 45000 },
    { id: "GE-05", category: "Office Supplies", desc: "Stationery, print rolls, and pantry restock", date: "2026-07-09", amount: 12400 },
    { id: "GE-06", category: "Travel & Transport", desc: "Site visit fuel and cab allowances", date: "2026-07-09", amount: 15600 }
  ];

  const totalGeneralExpenses = generalExpenses.reduce((sum, item) => sum + item.amount, 0);

  // Inventory Chart Data
  const inventoryChartData = {
    labels: [
      "Total Inventory",
      "Total Sales",
      "Purchase Returns",
      "Sale Returns",
      "Damaged",
      "Open Stock"
    ],
    datasets: [{
      label: "Units / Level",
      data: [280, 195, 15, 8, 4, 120],
      backgroundColor: "rgba(13, 148, 136, 0.8)", // Teal-600
      hoverBackgroundColor: "rgba(13, 148, 136, 1)",
      borderRadius: 6,
      barThickness: 16
    }]
  };

  // 7. Monthly Sales: Differentiating Direct vs. Project-related Sales
  const monthlySalesChartData = {
    labels: ["Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    datasets: [
      {
        label: "Direct Appliance Sales (₹)",
        data: [120000, 180000, 150000, 240000, 210000, 290000],
        backgroundColor: "rgba(13, 148, 136, 0.85)", // Teal
        borderColor: "rgba(13, 148, 136, 1)",
        borderWidth: 1,
        borderRadius: 4
      },
      {
        label: "Project-Related Sales (₹)",
        data: [340000, 410000, 390000, 520000, 480000, 610000],
        backgroundColor: "rgba(217, 119, 6, 0.85)", // Gold/Amber
        borderColor: "rgba(217, 119, 6, 1)",
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  // 8. Lead Source Contribution (Walk-in, Social Media, On-field Marketing)
  const leadSourceChartData = {
    labels: ["Walk-in", "Social Media", "On-field Marketing"],
    datasets: [{
      data: [35, 45, 20], // percentages/counts
      backgroundColor: [
        "#0f172a", // Charcoal
        "#0d9488", // Teal
        "#d97706"  // Gold
      ],
      hoverOffset: 6,
      borderWidth: 0
    }]
  };

  // 9. Factory Orders (My Orders)
  const factoryOrders = [
    { orderId: "ORD-9844", orderedDate: "2026-06-12", receivedDate: "2026-06-25", status: "Delivered", expectedDays: 10 },
    { orderId: "ORD-9845", orderedDate: "2026-06-18", receivedDate: "2026-06-30", status: "Delivered", expectedDays: 10 },
    { orderId: "ORD-9848", orderedDate: "2026-06-20", receivedDate: "2026-07-05", status: "Delivered", expectedDays: 12 },
    { orderId: "ORD-9852", orderedDate: "2026-06-28", receivedDate: "-", status: "In Transit", expectedDays: 8 },
    { orderId: "ORD-9855", orderedDate: "2026-07-02", receivedDate: "-", status: "Delayed", expectedDays: 5 }
  ];

  // Calculate Delay
  const calculateDelay = (order) => {
    const ordered = new Date(order.orderedDate);
    const expected = new Date(ordered.getTime() + order.expectedDays * 24 * 60 * 60 * 1000);
    
    if (order.status === "Delivered") {
      const received = new Date(order.receivedDate);
      const diffTime = received - expected;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } else {
      // For pending or delayed
      const today = new Date();
      const diffTime = today - expected;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-200 border-t-teal-600" />
          <p className="text-sm text-slate-500 font-medium animate-pulse">Loading Owner Intel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-8 overflow-x-hidden animate-in fade-in duration-500">
      
      {/* Page 1 Export Section */}
      <div ref={page1Ref} className="flex flex-col gap-6 bg-slate-50/50 p-4 -m-4 rounded-xl mb-4">
        
        {/* 1. Header Section */}
        <div className="relative overflow-hidden w-full h-44 bg-slate-900 border border-slate-200 rounded-2xl flex items-center justify-between shadow-md">
          {/* Background Overlay */}
          <div className="absolute inset-0 bg-black/40 z-10" />
          <img
            src="/dashboard_banner.png"
            alt="Elegant Kitchen Design Banner"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
          />
          
          {/* Title Content */}
          <div className="pl-10 z-20 text-white flex flex-col gap-1.5">
            <span className="text-xs uppercase tracking-widest text-teal-300 font-semibold">Strategic Management Panel</span>
            <h1 className="font-serif font-medium text-4xl text-white tracking-wide drop-shadow-md">
              Verve Kitchens
            </h1>
            <p className="text-sm text-slate-200 font-light mt-1 font-body drop-shadow-sm italic">
              "Transforming spaces, elevating lifestyles."
            </p>
          </div>

          {/* Right Details / Expandable Search Button */}
          <div className="pr-10 z-20 flex items-center gap-4">
            <div className="relative">
              {!showWorkflowSearch ? (
                <button 
                  onClick={() => setShowWorkflowSearch(true)}
                  className="flex items-center justify-center p-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-300 text-white shadow-lg"
                  title="Search Project Workflow"
                >
                  <Search size={20} />
                </button>
              ) : (
                <form 
                  onSubmit={handleWorkflowSearchSubmit}
                  className="flex items-center bg-white rounded-full border border-slate-200 shadow-xl overflow-hidden animate-in slide-in-from-right-10 duration-300"
                >
                  <input 
                    type="text"
                    placeholder="Enter custom ID or Name..."
                    value={workflowSearch}
                    onChange={(e) => setWorkflowSearch(e.target.value)}
                    className="pl-4 pr-2 py-2 text-sm text-slate-800 focus:outline-none w-52 font-medium"
                  />
                  <button 
                    type="submit" 
                    className="p-2 text-teal-600 hover:text-teal-800 transition-colors"
                  >
                    <Search size={18} />
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setShowWorkflowSearch(false);
                      clearWorkflowSearch();
                    }}
                    className="p-2 text-slate-400 hover:text-slate-600 border-l border-slate-100"
                  >
                    <X size={18} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Filters and Search Bar Section */}
        <div className="flex w-full mt-[-10px] pr-2">
          <div className="flex-1">
            <FilterSection 
              search={search}
              setSearch={setSearch}
              filters={filters}
              setFilters={(key, value) => setFilters(prev => {
                if (typeof key === 'object') {
                  return { ...prev, ...key };
                }
                return { ...prev, [key]: value };
              })}
              statusOptions={[]} 
              hideFilterButton={true} 
              customActions={
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="relative flex items-center gap-2 bg-white text-slate-700 px-4 py-2 rounded-lg border border-slate-200 shadow-sm hover:bg-slate-50 hover:shadow hover:text-brand-blue transition-all duration-300 font-medium text-sm font-sans"
                >
                  {isExporting ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-brand-blue rounded-full animate-spin" />
                  ) : (
                    <Download size={18} />
                  )}
                  <span>{isExporting ? 'Exporting PDF...' : 'Export to PDF'}</span>
                </button>
              }
            />
          </div>
        </div>

      {/* 2. Workflow Tracker Dashboard Banner */}
      {(trackedProject || workflowError) && (
        <div className="card p-6 border-l-4 border-l-teal-600 bg-white shadow-md rounded-xl animate-in slide-in-from-top duration-300">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-semibold text-slate-800 text-lg font-serif">Project Workflow Tracker</h3>
              <p className="text-xs text-slate-400 mt-0.5">Real-time level tracking for target project ID</p>
            </div>
            <button 
              onClick={clearWorkflowSearch}
              className="p-1 rounded-full text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {workflowError ? (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg text-sm font-medium border border-red-100">
              <AlertCircle size={18} />
              <span>{workflowError}</span>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Project info card */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50/70 border border-slate-100 rounded-lg text-sm">
                <div>
                  <span className="text-slate-400 block text-xs">Verve ID / Project Name</span>
                  <span className="font-semibold text-slate-800 block mt-0.5">
                    {trackedProject.customId || trackedProject.projectId || trackedProject.projectNumber || trackedProject._id}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-xs">Customer Name</span>
                  <span className="font-semibold text-slate-800 block mt-0.5">{trackedProject.customerName || trackedProject.name || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-xs">Current Stage</span>
                  <span className="font-semibold text-teal-700 uppercase block mt-0.5 text-xs bg-teal-50 border border-teal-100 w-fit px-2 py-0.5 rounded">
                    {trackedProject.leadStatus || trackedProject.status || "new"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-xs">Estimate Budget</span>
                  <span className="font-semibold text-slate-800 block mt-0.5">
                    {formatCurrency(trackedProject.estimatedBudget || trackedProject.projectValue || trackedProject.quotationAmount || trackedProject.budget || 0)}
                  </span>
                </div>
              </div>

              {/* Progress Steps levels */}
              <div className="relative mt-2">
                <div className="absolute top-1/2 left-0 right-0 h-1 bg-slate-100 -translate-y-1/2 z-0 rounded-full" />
                <div 
                  className="absolute top-1/2 left-0 h-1 bg-teal-500 -translate-y-1/2 z-0 rounded-full transition-all duration-500" 
                  style={{ width: `${(getProjectWorkflowLevel(trackedProject.leadStatus || trackedProject.status) / (WORKFLOW_LEVELS.length - 1)) * 100}%` }}
                />

                <div className="relative z-10 flex justify-between">
                  {WORKFLOW_LEVELS.map((level, index) => {
                    const projectLevel = getProjectWorkflowLevel(trackedProject.leadStatus || trackedProject.status);
                    const isCompleted = index <= projectLevel;
                    const isActive = index === projectLevel;

                    return (
                      <div key={level.id} className="flex flex-col items-center text-center max-w-[120px]">
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                            isCompleted 
                              ? "bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-100" 
                              : "bg-white border-slate-200 text-slate-400"
                          } ${isActive ? "ring-4 ring-teal-50 border-teal-600" : ""}`}
                        >
                          {isCompleted ? <Check size={18} /> : <span className="text-sm font-semibold">{index + 1}</span>}
                        </div>
                        <span className={`text-xs font-semibold mt-2.5 ${isCompleted ? "text-slate-900" : "text-slate-400"}`}>
                          {level.label}
                        </span>
                        <span className="text-[10px] text-slate-400 mt-0.5 leading-tight font-medium hidden sm:block">
                          {level.desc}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. Financial Summary */}
      <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg font-serif">Financial Summary</h3>
            <p className="text-xs text-slate-400 mt-0.5">Overview of revenues, receipts, and expenditures</p>
          </div>
          <span className="text-xs font-semibold text-teal-600 bg-teal-50 border border-teal-100 px-2.5 py-1 rounded-full">
            INR (₹) Account base
          </span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {financialMetrics.map((metric, idx) => (
            <div 
              key={idx} 
              className={`rounded-xl p-5 border flex flex-col transition-all duration-300 hover:scale-[1.02] shadow-sm bg-gradient-to-br ${metric.color}`}
            >
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{metric.label}</span>
              <div className="text-2xl font-bold font-serif mt-2.5 tracking-tight">
                {formatCurrency(metric.value)}
              </div>
              <p className="text-[11px] text-slate-400 mt-2 font-medium leading-relaxed">{metric.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 4. Operations Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {operationsMetrics.map((metric, idx) => (
          <div key={idx} className="card p-5 flex items-center justify-between border border-slate-150 rounded-xl bg-white shadow-sm hover:shadow transition-shadow">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{metric.label}</span>
              <span className="text-2xl font-bold text-slate-900 mt-1 font-serif">
                {metric.isCurrency ? formatCurrency(metric.value) : metric.value}
              </span>
            </div>
            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center border border-teal-100/50">
              {metric.icon}
            </div>
          </div>
        ))}
      </div>

      {/* 5. Team Attendance & Workload Distribution (Combined) */}
      <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-2">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg font-serif">Team Attendance & Workload Distribution</h3>
            <p className="text-xs text-slate-400 mt-0.5">Real-time status tracking and project queue mapping for staff members</p>
          </div>
          <span className="text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-100 px-3 py-1 rounded-full w-fit">
            Today: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        <div className="overflow-x-auto min-w-0 w-full">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase">
                <th className="py-3.5 font-semibold">Staff Name</th>
                <th className="py-3.5 font-semibold">Phone</th>
                <th className="py-3.5 font-semibold">Attendance</th>
                <th className="py-3.5 font-semibold text-center">Total Projects</th>
                <th className="py-3.5 font-semibold text-center">Assigned</th>
                <th className="py-3.5 font-semibold text-center">Ongoing</th>
                <th className="py-3.5 font-semibold text-right">Completed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-medium">
              {combinedTeamList.map((member, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                  <td className="py-3.5 text-slate-800 font-semibold">{member.name}</td>
                  <td className="py-3.5 text-slate-500 font-body text-xs">{member.phone}</td>
                  <td className="py-3.5">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                      member.status === "Present" 
                        ? "bg-teal-50 border-teal-100 text-teal-700" 
                        : "bg-slate-50 border-slate-200 text-slate-400"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${member.status === "Present" ? "bg-teal-500" : "bg-slate-400"}`} />
                      {member.status}
                    </span>
                  </td>
                  <td className="py-3.5 text-center text-slate-700 font-bold">{member.totalProjects}</td>
                  <td className="py-3.5 text-center text-slate-500">{member.assigned}</td>
                  <td className="py-3.5 text-center text-amber-600 font-semibold">{member.ongoing}</td>
                  <td className="py-3.5 text-right text-teal-600 font-bold">{member.completed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {/* Page 2 Export Section */}
    <div ref={page2Ref} className="flex flex-col gap-6 bg-slate-50/50 p-4 -m-4 rounded-xl">

      {/* 6. Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Monthly Sales (Direct vs. Project) */}
        <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm lg:col-span-2">
          <div className="mb-4">
            <h3 className="font-semibold text-slate-800 text-lg font-serif">Monthly Appliance Sales</h3>
            <p className="text-xs text-slate-400 mt-0.5">Direct appliance purchases vs. comprehensive project fit-outs</p>
          </div>
          <div className="h-64 relative">
            <Bar 
              data={monthlySalesChartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11, family: "'DM Sans', sans-serif" } } }
                },
                scales: {
                  y: {
                    ticks: { callback: (value) => '₹' + (value / 1000) + 'k' },
                    grid: { color: 'rgba(0,0,0,0.03)' }
                  },
                  x: { grid: { display: false } }
                }
              }} 
            />
          </div>
        </div>

        {/* Lead Source Doughnut Chart */}
        <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm">
          <div className="mb-4">
            <h3 className="font-semibold text-slate-800 text-lg font-serif">Lead Source Contribution</h3>
            <p className="text-xs text-slate-400 mt-0.5">Share percentage of different lead pathways</p>
          </div>
          <div className="h-48 relative flex justify-center items-center">
            <Doughnut 
              data={leadSourceChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                cutout: '65%'
              }}
            />
            {/* Center Text overlay */}
            <div className="absolute flex flex-col items-center">
              <span className="text-2xl font-bold text-slate-800 font-serif">100%</span>
              <span className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Lead Ratio</span>
            </div>
          </div>
          
          {/* Custom legends displaying percentages */}
          <div className="flex flex-col gap-2 mt-4 text-xs font-semibold text-slate-600">
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-900" /> Walk-in</span>
              <span className="text-slate-800">35%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-teal-600" /> Social Media</span>
              <span className="text-slate-800">45%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-600" /> On-field Marketing</span>
              <span className="text-slate-800">20%</span>
            </div>
          </div>
        </div>

      </div>

      {/* 7. General Expenses Monthly Tracker */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Expenses List Table */}
        <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm lg:col-span-2 flex flex-col h-[360px]">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-semibold text-slate-800 text-lg font-serif">General Expenses (Non-Project)</h3>
              <p className="text-xs text-slate-400 mt-0.5">Office rental, software licensing, utilities, and marketing</p>
            </div>
            <div className="text-right">
              <span className="text-[10px] uppercase text-slate-400 block font-bold">Total Operational Cost</span>
              <span className="text-base font-bold text-slate-900 font-serif">{formatCurrency(totalGeneralExpenses)}</span>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0 pr-1">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase">
                  <th className="py-2 font-semibold">Category</th>
                  <th className="py-2 font-semibold">Details</th>
                  <th className="py-2 font-semibold">Date</th>
                  <th className="py-2 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 font-medium">
                {generalExpenses.map((exp) => (
                  <tr key={exp.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 text-slate-800 font-semibold text-xs bg-slate-50 w-28 px-2 rounded-lg border border-slate-100">{exp.category}</td>
                    <td className="py-3 text-slate-500 text-xs pl-2">{exp.desc}</td>
                    <td className="py-3 text-slate-400 text-xs font-body">{exp.date}</td>
                    <td className="py-3 text-right text-slate-800 font-bold font-body">{formatCurrency(exp.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Inventory Visualizer */}
        <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm h-[360px] flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg font-serif">Inventory</h3>
            <p className="text-xs text-slate-400 mt-0.5">Visual representation of stock levels and trade volume</p>
          </div>
          <div className="h-56 relative mt-4">
            <Bar 
              data={inventoryChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y', // Horizontal bars
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  x: { ticks: { font: { size: 9 } } },
                  y: { grid: { display: false } }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* 8. My Orders (Factory Orders Tracking) */}
      <div className="card p-6 border border-slate-150 rounded-xl bg-white shadow-sm flex flex-col">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-800 text-lg font-serif">Factory Orders (My Orders)</h3>
          <p className="text-xs text-slate-400 mt-0.5">Track production timeline and delay flags for cabinet components</p>
        </div>

        <div className="overflow-x-auto min-w-0 w-full">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase">
                <th className="py-2.5 font-semibold">Order ID</th>
                <th className="py-2.5 font-semibold">Ordered Date</th>
                <th className="py-2.5 font-semibold">Received Date</th>
                <th className="py-2.5 font-semibold">Status</th>
                <th className="py-2.5 font-semibold text-right">Delay (Days)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-medium">
              {factoryOrders.map((order) => {
                const delay = calculateDelay(order);
                const isDelayed = delay > 0;

                return (
                  <tr key={order.orderId} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3.5 text-slate-800 font-bold">{order.orderId}</td>
                    <td className="py-3.5 text-slate-500 font-body">{order.orderedDate}</td>
                    <td className="py-3.5 text-slate-500 font-body">{order.receivedDate}</td>
                    <td className="py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        order.status === "Delivered" 
                          ? "bg-teal-50 border-teal-100 text-teal-700" 
                          : order.status === "Delayed"
                            ? "bg-red-50 border-red-100 text-red-700 animate-pulse"
                            : "bg-amber-50 border-amber-100 text-amber-700"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          order.status === "Delivered" 
                            ? "bg-teal-500" 
                            : order.status === "Delayed"
                              ? "bg-red-500"
                              : "bg-amber-500"
                        }`} />
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3.5 text-right font-bold">
                      {isDelayed ? (
                        <span className="text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded font-body text-xs">
                          +{delay} Days
                        </span>
                      ) : (
                        <span className="text-slate-400 font-body text-xs">
                          On Time
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
  );
};
